import type { OptionType } from "components/inputs";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "utils/hooks";

import type { ExtendedOptionType, FinanceFilters } from "../types";
import { REPORT_TYPE_OPTIONS } from "../components/ReportTypeHeaderSelector/ReportTypeHeaderSelector";

/**
 * hook to manage finance filter state via URL parameters
 * reads and writes filter state to/from URL search params
 */
export const useFinanceFilterState = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useQuery();

  // parse filters from URL search params
  const filters = useMemo((): FinanceFilters => {
    const reportTypeValue = searchParams.get("reportType");
    // default to INVOICE_LIST if no report type is specified
    const reportType = reportTypeValue
      ? REPORT_TYPE_OPTIONS.find((opt) => opt.value === reportTypeValue) || null
      : REPORT_TYPE_OPTIONS.find((opt) => opt.value === "INVOICE_LIST") || null;

    const parseArrayParam = (key: string): string[] => {
      const value = searchParams.get(key);
      return value ? value.split(",").filter(Boolean) : [];
    };

    const parseOptionsParam = (key: string): OptionType[] => {
      const values = parseArrayParam(key);
      return values.map((v) => ({ label: v, value: v }));
    };

    // parse clients with base64 decoding
    // format: dbId::clientId::label
    const parseClientOptions = (): ExtendedOptionType[] => {
      const values = parseArrayParam("clients");
      return values
        .map((encodedValue) => {
          try {
            const decoded = atob(encodedValue);
            const [dbId, clientId, ...labelParts] = decoded.split("::");
            const label = labelParts.join("::"); // rejoin in case :: appears in label
            return {
              label,
              value: dbId, // database ID for API
              clientId, // client_id string identifier
            } as ExtendedOptionType;
          } catch (e) {
            console.error("Failed to decode client value:", encodedValue, e);
            return null;
          }
        })
        .filter((option): option is ExtendedOptionType => option !== null);
    };

    const clients = parseClientOptions();
    const serviceTypes = parseOptionsParam("serviceTypes");
    const invoiceStatuses = parseOptionsParam("invoiceStatuses");
    const fileIds = parseOptionsParam("fileIds");
    const invoiceNumbers = parseOptionsParam("invoiceNumbers");

    const paymentStatusValue = searchParams.get("paymentStatus");
    const paymentStatus = paymentStatusValue
      ? ({ label: paymentStatusValue, value: paymentStatusValue } as OptionType)
      : null;

    const clientServiceMapStr = searchParams.get("clientServiceMap");
    let clientServiceMap = {};
    if (clientServiceMapStr) {
      try {
        clientServiceMap = JSON.parse(clientServiceMapStr);
      } catch (e) {
        console.error("Error parsing clientServiceMap:", e);
      }
    }

    return {
      report_type: reportType,
      posted_date_start: searchParams.get("postedDateStart"),
      posted_date_end: searchParams.get("postedDateEnd"),
      clients,
      client_search_term: "",
      service_types: serviceTypes,
      client_service_map: clientServiceMap,
      invoice_statuses: invoiceStatuses,
      file_ids: fileIds,
      payment_status: paymentStatus,
      invoice_numbers: invoiceNumbers,
      payment_date_from: searchParams.get("paymentDateFrom"),
      payment_date_to: searchParams.get("paymentDateTo"),
      service_created_from: searchParams.get("serviceCreatedFrom"),
      service_created_to: searchParams.get("serviceCreatedTo"),
      file_opened_on_from: searchParams.get("fileOpenedFrom"),
      file_opened_on_to: searchParams.get("fileOpenedTo"),
      service_date_from: searchParams.get("serviceDateFrom"),
      service_date_to: searchParams.get("serviceDateTo"),
      order_by: searchParams.get("orderBy"),
      // Section 111 invoice filters (for Invoice List)
      icn: searchParams.get("icn"),
      claim_id: searchParams.get("claimId"),
      claim_number: searchParams.get("claimNumber"),
      rre_name: searchParams.get("rreName"),
      rre_id: searchParams.get("rreIdSearch"),
      s111_claimant_name: searchParams.get("s111ClaimantName"),
      send_date_from: searchParams.get("sendDateFrom"),
      send_date_to: searchParams.get("sendDateTo"),
      doi_from: searchParams.get("doiFrom"),
      doi_to: searchParams.get("doiTo"),
      tpoc_from: searchParams.get("tpocFrom"),
      tpoc_to: searchParams.get("tpocTo"),
      medicare_from: searchParams.get("medicareFrom"),
      medicare_to: searchParams.get("medicareTo"),
      // Section 111 Invoicing filters
      rre: searchParams.get("rreId")
        ? ({
            label: searchParams.get("rreLabel") || searchParams.get("rreId")!,
            value: parseInt(searchParams.get("rreId")!, 10),
          } as OptionType)
        : null,
      file_type: searchParams.get("fileType")
        ? ({
            label: searchParams.get("fileType") === "C" ? "Claim" : "Query",
            value: searchParams.get("fileType")!,
          } as OptionType)
        : null,
      hide_invoiced: searchParams.get("hideInvoiced") !== "false",
      nsa_filter: searchParams.get("nsaFilter")
        ? ({
            label: searchParams.get("nsaFilter")!,
            value: searchParams.get("nsaFilter")!,
          } as OptionType)
        : null,
      action_filter: searchParams.get("actionFilter")
        ? ({
            label:
              searchParams.get("actionFilter") === "0"
                ? "Add"
                : searchParams.get("actionFilter") === "1"
                  ? "Delete"
                  : "Update",
            value: searchParams.get("actionFilter")!,
          } as OptionType)
        : null,
    };
  }, [location.search, searchParams]);

  // update URL with new filters
  const updateFilters = useCallback(
    (newFilters: Partial<FinanceFilters>, replace: boolean = false) => {
      const params = new URLSearchParams(searchParams);

      // helper to set param if value exists
      const setParam = (key: string, value: string | null | undefined) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      };

      // helper to set array param
      const setArrayParam = (key: string, values: OptionType[] | undefined) => {
        if (values && values.length > 0) {
          params.set(key, values.map((v) => v.value).join(","));
        } else {
          params.delete(key);
        }
      };

      // helper to set client array param with base64 encoding
      // format: dbId::clientId::label
      const setClientArrayParam = (
        key: string,
        values: ExtendedOptionType[] | undefined,
      ) => {
        if (values && values.length > 0) {
          const encoded = values.map((option) =>
            btoa(`${option.value}::${option.clientId}::${option.label}`),
          );
          params.set(key, encoded.join(","));
        } else {
          params.delete(key);
        }
      };

      // update params based on newFilters
      if (newFilters.report_type !== undefined) {
        setParam("reportType", newFilters.report_type?.value?.toString());
      }
      if (newFilters.posted_date_start !== undefined) {
        setParam("postedDateStart", newFilters.posted_date_start);
      }
      if (newFilters.posted_date_end !== undefined) {
        setParam("postedDateEnd", newFilters.posted_date_end);
      }
      if (newFilters.clients !== undefined) {
        setClientArrayParam(
          "clients",
          newFilters.clients as ExtendedOptionType[],
        );
      }
      if (newFilters.service_types !== undefined) {
        setArrayParam("serviceTypes", newFilters.service_types);
      }
      if (newFilters.invoice_statuses !== undefined) {
        setArrayParam("invoiceStatuses", newFilters.invoice_statuses);
      }
      if (newFilters.file_ids !== undefined) {
        setArrayParam("fileIds", newFilters.file_ids);
      }
      if (newFilters.payment_status !== undefined) {
        setParam("paymentStatus", newFilters.payment_status?.value?.toString());
      }
      if (newFilters.invoice_numbers !== undefined) {
        setArrayParam("invoiceNumbers", newFilters.invoice_numbers);
      }
      if (newFilters.payment_date_from !== undefined) {
        setParam("paymentDateFrom", newFilters.payment_date_from);
      }
      if (newFilters.payment_date_to !== undefined) {
        setParam("paymentDateTo", newFilters.payment_date_to);
      }
      if (newFilters.service_created_from !== undefined) {
        setParam("serviceCreatedFrom", newFilters.service_created_from);
      }
      if (newFilters.service_created_to !== undefined) {
        setParam("serviceCreatedTo", newFilters.service_created_to);
      }
      if (newFilters.file_opened_on_from !== undefined) {
        setParam("fileOpenedFrom", newFilters.file_opened_on_from);
      }
      if (newFilters.file_opened_on_to !== undefined) {
        setParam("fileOpenedTo", newFilters.file_opened_on_to);
      }
      if (newFilters.service_date_from !== undefined) {
        setParam("serviceDateFrom", newFilters.service_date_from);
      }
      if (newFilters.service_date_to !== undefined) {
        setParam("serviceDateTo", newFilters.service_date_to);
      }
      if (newFilters.client_service_map !== undefined) {
        const mapStr = JSON.stringify(newFilters.client_service_map);
        if (mapStr && mapStr !== "{}") {
          params.set("clientServiceMap", mapStr);
        } else {
          params.delete("clientServiceMap");
        }
      }
      // Section 111 invoice filters (for Invoice List)
      if (newFilters.icn !== undefined) {
        setParam("icn", newFilters.icn);
      }
      if (newFilters.claim_id !== undefined) {
        setParam("claimId", newFilters.claim_id);
      }
      if (newFilters.claim_number !== undefined) {
        setParam("claimNumber", newFilters.claim_number);
      }
      if (newFilters.rre_name !== undefined) {
        setParam("rreName", newFilters.rre_name);
      }
      if (newFilters.rre_id !== undefined) {
        setParam("rreIdSearch", newFilters.rre_id);
      }
      if (newFilters.s111_claimant_name !== undefined) {
        setParam("s111ClaimantName", newFilters.s111_claimant_name);
      }
      if (newFilters.send_date_from !== undefined) {
        setParam("sendDateFrom", newFilters.send_date_from);
      }
      if (newFilters.send_date_to !== undefined) {
        setParam("sendDateTo", newFilters.send_date_to);
      }
      if (newFilters.doi_from !== undefined) {
        setParam("doiFrom", newFilters.doi_from);
      }
      if (newFilters.doi_to !== undefined) {
        setParam("doiTo", newFilters.doi_to);
      }
      if (newFilters.tpoc_from !== undefined) {
        setParam("tpocFrom", newFilters.tpoc_from);
      }
      if (newFilters.tpoc_to !== undefined) {
        setParam("tpocTo", newFilters.tpoc_to);
      }
      if (newFilters.medicare_from !== undefined) {
        setParam("medicareFrom", newFilters.medicare_from);
      }
      if (newFilters.medicare_to !== undefined) {
        setParam("medicareTo", newFilters.medicare_to);
      }
      // Section 111 Invoicing filters
      if (newFilters.rre !== undefined) {
        setParam("rreId", newFilters.rre?.value?.toString());
        setParam("rreLabel", newFilters.rre?.label);
      }
      if (newFilters.file_type !== undefined) {
        setParam("fileType", newFilters.file_type?.value?.toString());
      }
      if (newFilters.hide_invoiced !== undefined) {
        setParam(
          "hideInvoiced",
          newFilters.hide_invoiced === false ? "false" : null,
        );
      }
      if (newFilters.nsa_filter !== undefined) {
        setParam("nsaFilter", newFilters.nsa_filter?.value?.toString());
      }
      if (newFilters.action_filter !== undefined) {
        setParam("actionFilter", newFilters.action_filter?.value?.toString());
      }

      // navigate with new params
      navigate(`${location.pathname}?${params.toString()}`, { replace });
    },
    [searchParams, location.pathname, navigate],
  );

  // set a single filter
  const setFilter = useCallback(
    (key: keyof FinanceFilters, value: unknown) => {
      updateFilters({ [key]: value });
    },
    [updateFilters],
  );

  // remove a filter
  const removeFilter = useCallback(
    (key: keyof FinanceFilters) => {
      updateFilters({ [key]: null });
    },
    [updateFilters],
  );

  // clear all filters except report_type
  const clearFilters = useCallback(() => {
    const params = new URLSearchParams();
    params.set(
      "reportType",
      filters.report_type?.value?.toString() || "INVOICE_LIST",
    );
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [filters.report_type, location.pathname, navigate]);

  // get a specific filter value
  const getFilterValue = useCallback(
    (key: keyof FinanceFilters) => {
      return filters[key];
    },
    [filters],
  );

  return {
    filters,
    setFilter,
    removeFilter,
    clearFilters,
    updateFilters,
    getFilterValue,
  };
};
